import { Component } from 'react'
import * as styles from 'styles/atoms/charts/PieChart.css'
import * as cn from 'classnames'
import * as sumBy from 'lodash/sumBy'
import { PieChartSector } from 'interfaces/chart'
import { IS_MOBILE } from 'helpers/userAgentHelpers'

type Id = number | string

// please take care that sum of data[i].value at least 100
// JS is not perfect and has rounding errors
export type PieChartProps = {
  data: PieChartSector[]
  mainColor: string
  mainFontColor: string
  viewBoxWidth: number
  onSectorHover: (id: Id | null) => void
  onSectorClick?: (id: Id | null) => void
}

export const SECTION_EXPAND_PX = 12

const SECTOR_STROKE_WIDTH = 0
const START_ANGLE = 270
const MOST_BOTTOM_POINT_ANGLE = 90

type GetTextPosition = {
  isLarge: boolean
  vector: number
  text: string
  startAngle: number
  endAngle: number
  center: number
}

type TextLines = [string] | [string, string]
type TextPosition = {
  lines: [string] | [string, string]
  x: number
  y: number
  translationX: string
  translationY: string
}

// tooltip in terms of pie charts is a sector's label positioned outside of the pie
type GetTooltipPosition = {
  x1: number
  y1: number
  x2: number
  y2: number
  vector: number
  textLength: number
  startAngle: number
  endAngle: number
  center: number
}

type TooltipPosition = {
  x: number
  y: number
  translationX: string
  translationY: string
}

type GetTooltipArg = {
  positionParams: GetTooltipPosition
  isTooltipShown: boolean
  tooltipFontColor: string
  title: string
  subtitle: string
}

const TURNED_MOST_BOTTOM_POINT_ANGLE = (MOST_BOTTOM_POINT_ANGLE + 90) % 360
/**
 * Return if sector is the most bottom
 * @returns Boolean
 */
const hasMostBottomPoint = (startAngle: number, endAngle: number): boolean => {
  /*
    Turn the coordinate system on 90deg. 0deg is the most upper circle point.
   */
  const turnedStartAngle = (startAngle + 90) % 360
  const turnedEndAngle = turnedStartAngle + (endAngle - startAngle)

  return turnedStartAngle <= TURNED_MOST_BOTTOM_POINT_ANGLE &&
    turnedEndAngle >= TURNED_MOST_BOTTOM_POINT_ANGLE
}

const MAX_NONE_LARGE_SLICE_ANGLE = 120
const getTextRenderSlice = (startAngle: number, endAngle: number, vector: number): [number, number] => {
  if (endAngle - startAngle < MAX_NONE_LARGE_SLICE_ANGLE) {
    return [startAngle, endAngle]
  }

  return [
    vector - MAX_NONE_LARGE_SLICE_ANGLE / 2,
    vector + MAX_NONE_LARGE_SLICE_ANGLE / 2,
  ]
}

const MAX_ONE_LINE_LENGTH = 14
/**
 * Split string longer than MAX_ONE_LINE_LENGTH into 2 lines if it's possible
 * @param text
 */
const getTextLines = (text: string): TextLines => {
  if (text.length <= MAX_ONE_LINE_LENGTH) {
    return [text]
  }

  // need to join ampersand to previous part
  // ampersand should be on the first line
  const parts = text.split(' ').reduce((result, part) => {
    if (result.length > 0 && part === '&') {
      result[result.length - 1] += ' &'
    } else {
      result.push(part)
    }

    return result
  }, [] as string[])
  if (parts.length === 1) {
    return [parts[0]]
  }

  let firstLine = parts.slice(0, 1)[0]
  let lastLine = parts.slice(-1)[0]
  let middleParts = parts.slice(1, -1)
  while (middleParts.length > 0) {
    if (firstLine.length >= lastLine.length) {
      // if first line is equal or longer than second then prepend second
      const nextPart = middleParts.slice(-1)[0]
      middleParts = middleParts.slice(0, -1)
      lastLine = `${ nextPart } ${ lastLine }`
    } else {
      // another way append first line
      const nextPart = middleParts.slice(0, 1)[0]
      middleParts = middleParts.slice(1)
      firstLine = `${ firstLine } ${ nextPart }`
    }
  }

  return [firstLine, lastLine]
}

/**
 * Determines whether the text fits its sector or not
 */
const doesTextFitSector = (value: number, total: number, textLength: number): boolean =>
  (value / total) * 100 >= Math.max(MAX_ONE_LINE_LENGTH + 1, textLength)

class PieChart extends Component<PieChartProps, {}> {

  render () {
    const { data, viewBoxWidth, onSectorHover } = this.props
    const filterData = data.filter(({ value }) => value > 0)
    const viewBoxSize = viewBoxWidth + SECTION_EXPAND_PX * 2

    return (
      <svg
        viewBox={ `0 0 ${ viewBoxSize } ${ viewBoxSize }` }
        onMouseLeave={ () => onSectorHover(null) }
      >
        <g transform={ `translate(${ SECTION_EXPAND_PX }, ${ SECTION_EXPAND_PX })` }>
          { filterData.length < 2 ? this.getSector() : this.getSectors() }
        </g>
      </svg>
    )}

  /**
   * When there is only one item, pie chart renders like ellipse
   */
  private getSector () {
    const { data, viewBoxWidth, onSectorHover } = this.props
    const {
      id, color, title, subtitle, fontColor,
      isSelected: isEllipseSelected, isHighlighted: isEllipseHighlighted,
    } = data[0]
    const hasHighlightedSector = data.some(({ isHighlighted }) => isHighlighted)
    const hoveredSector = data.find(({ isHighlighted, isSelected }) => !isSelected && isHighlighted)
    const center = viewBoxWidth / 2

    const textTranslationX = `${ -title.length * 0.2 }rem`

    const handleClick = () => this.handleClick(id, isEllipseSelected, isEllipseHighlighted)

    const isEllipseShaded = (Boolean(hoveredSector) || hasHighlightedSector) && !isEllipseSelected && !isEllipseHighlighted
    const ellipseClassName = cn(styles.ellipse, {
      [styles.shaded]: isEllipseShaded,
    })

    const [, ...restCategories] = data

    const startAngle = START_ANGLE
    const endAngle = START_ANGLE
    const x1 = Math.round(center + center * Math.cos(Math.PI * startAngle / 180))
    const y1 = Math.round(center + center * Math.sin(Math.PI * startAngle / 180))

    const x2 = Math.round(center + center * Math.cos(Math.PI * endAngle / 180))
    const y2 = Math.round(center + center * Math.sin(Math.PI * endAngle / 180))

    const vector = (startAngle + 360 + (endAngle - startAngle) / 2) % 360

    return (
      <g>
        <g
          className={ ellipseClassName }
          onMouseEnter={ () => !IS_MOBILE && onSectorHover(id) }
          onMouseLeave={ () => !IS_MOBILE && onSectorHover(null) }
          onClick={ handleClick }
          style={{ transform: isEllipseSelected ? `scale(${ (viewBoxWidth + SECTION_EXPAND_PX) / viewBoxWidth })` : 'none' }}
        >

          <ellipse fill={ color } cx={ center } cy={ center } rx={ center } ry={ center } />

          <g style={{ transform: `translate(${ textTranslationX }, 0)` }}>
            <text
              className={ styles.text }
              fill={ fontColor }
              x={ center }
              y={ center }
            >
              <tspan>{ title }</tspan>
              <tspan x={ center } dy='11px'>
                { subtitle }
              </tspan>
            </text>
          </g>
        </g>

        { restCategories.map(({ title: categoryTitle, subtitle: categorySubTitle, isHighlighted, isSelected, tooltipFontColor }) => {
          let isTooltipShown = isHighlighted
          if (isSelected) {
            // prevent a case of overlap when 0-value category is selected and other 0-value category is hovered
            isTooltipShown = !(hoveredSector && hoveredSector.value === 0)
          }

          return this.getTooltip({
            positionParams: {
              x1,
              y1,
              x2,
              y2,
              vector,
              textLength: categoryTitle.length,
              startAngle: 0,
              endAngle: 0,
              center,
            },
            isTooltipShown,
            tooltipFontColor,
            title: categoryTitle,
            subtitle: categorySubTitle,
          })
        })}
      </g>
    )
  }

  private getTextPosition ({
    startAngle, endAngle, center,
    text,
    isLarge,
    vector,
  }: GetTextPosition): TextPosition {
    const radius = center - SECTION_EXPAND_PX

    // shift text to bounce of pie
    const vectorRad = vector / 180 * Math.PI
    const radX = Math.cos(vectorRad) * radius + center
    const radY = Math.sin(vectorRad) * radius + center

    const lines = getTextLines(text)
    const maxTextLength = Math.max(lines[0].length, (lines[1] || '').length)

    // some corrections: consider width & height of text
    const translationX = `${ Math.floor(maxTextLength / 9) * 0.1 - maxTextLength * 0.15 }rem`
    const translationY = `${ -Math.floor((radY - center) / 10) * (lines.length > 1 ? 1.5 : 1) }px`

    if (!isLarge) {
      const [startSector, endSector] = getTextRenderSlice(startAngle, endAngle, vector)

      const x1 = Math.round(center + center * Math.cos(Math.PI * startSector / 180))
      const y1 = Math.round(center + center * Math.sin(Math.PI * startSector / 180))
      const x2 = Math.round(center + center * Math.cos(Math.PI * endSector / 180))
      const y2 = Math.round(center + center * Math.sin(Math.PI * endSector / 180))

      return {
        lines,
        x: Math.round((x1 + x2 + center + radX * 2) / 5),
        y: Math.round((y1 + y2 + center + radY * 2) / 5),
        translationX,
        translationY,
      }
    }

    return {
      lines,
      x: Math.round((center + radX) / 2),
      y: Math.round((center + radY) / 2),
      translationX,
      translationY,
    }
  }

  private getTooltipPosition ({
    x1, y1, x2, y2,
    vector, textLength,
    startAngle, endAngle, center,
  }: GetTooltipPosition): TooltipPosition {
    // we should render below sector if sector is the most bottom
    // or we have issues with rendering
    const hasBottom = hasMostBottomPoint(startAngle, endAngle)
    const tooltipVector = hasBottom ? MOST_BOTTOM_POINT_ANGLE : vector
    const atLeft = tooltipVector >= 90 && tooltipVector <= 270
    const atTop = tooltipVector >= 180 || tooltipVector <= 0
    const [x, y] = hasBottom ?
      [
        Math.round(center + center * Math.cos(Math.PI * MOST_BOTTOM_POINT_ANGLE / 180)),
        Math.round(center + center * Math.sin(Math.PI * MOST_BOTTOM_POINT_ANGLE / 180)),
      ] :
      (
        atLeft === atTop ? [x2, y2] : [x1, y1]
      )

    // translations will be responsive, so measure it in rem
    const vectorRad = tooltipVector / 180 * Math.PI
    const translateX = Math.cos(vectorRad) * 1.5 - textLength * 0.125
    const translateY = Math.sin(vectorRad) * 1.5 + (atTop ? -0.75 : 0.25)

    return {
      x,
      y,
      translationX: `${ translateX }rem`,
      translationY: `${ translateY }rem`,
    }
  }

  private getTooltip ({ isTooltipShown, tooltipFontColor, title, subtitle, positionParams }: GetTooltipArg) {
    const tooltipPosition = this.getTooltipPosition(positionParams)

    return (
      <g
        key={ title }
        className={ cn(styles.tooltip, { [styles.shown]: isTooltipShown }) }
        style={{ transform: `translate(${ tooltipPosition.translationX }, ${ tooltipPosition.translationY })` }}
      >
        <text
          className={ styles.text }
          fill={ tooltipFontColor }
          x={ tooltipPosition.x }
          y={ tooltipPosition.y }
        >
          <tspan>{ title }</tspan>
          <tspan x={ tooltipPosition.x } dy='11px' data-qa-marker='pie-chart-value' data-qa-type='outside'>
            { subtitle }
          </tspan>
        </text>
      </g>
    )
  }

  private getSectors () {
    const {
      data, viewBoxWidth,
      onSectorHover,
      mainColor, mainFontColor,
    } = this.props

    const total = sumBy(data, (item) => item.value)
    const hasHighlightedSector = data.some(({ isHighlighted }) => isHighlighted)
    const hasHoveredSector = data.some(({ isHighlighted, isSelected }) => !isSelected && isHighlighted)
    const hasHoveredTooltip = data.some(({ isSelected, isHighlighted, value, title }) =>
      !isSelected && isHighlighted && !doesTextFitSector(value, total, title.length))

    const center = viewBoxWidth / 2
    let startAngle = START_ANGLE
    let endAngle = startAngle

    return (
      data.map(({
        id, value, title, subtitle,
        color, fontColor, tooltipFontColor,
        isSelected, isShaded, isHighlighted,
      }) => {
        const angle = 360 * value / total
        // sector more than 50%
        const largeArc = (value / total) <= 0.5 ? 0 : 1

        startAngle = endAngle
        endAngle = startAngle + angle

        const vector = (startAngle + 360 + (endAngle - startAngle) / 2) % 360
        const vectorRad = vector / 180 * Math.PI

        const x1 = Math.round(center + center * Math.cos(Math.PI * startAngle / 180))
        const y1 = Math.round(center + center * Math.sin(Math.PI * startAngle / 180))

        const x2 = Math.round(center + center * Math.cos(Math.PI * endAngle / 180))
        const y2 = Math.round(center + center * Math.sin(Math.PI * endAngle / 180))

        const dPath =
          `M${ center },${ center } ` +
          `L${ x1 },${ y1 } ` +
          `A${ center },${ center } 0 ${ largeArc },1 ${ x2 },${ y2 } ` +
          `z`

        // shift selected sector
        let translateX = 0
        let translateY = 0
        if (isHighlighted) {
          translateX = Math.cos(vectorRad) * SECTION_EXPAND_PX
          translateY = Math.sin(vectorRad) * SECTION_EXPAND_PX
        }

        const handleMouseEnter = () => !IS_MOBILE && onSectorHover(isSelected ? null : id)
        const handleClick = () => this.handleClick(id, isSelected, isHighlighted)

        const textLength = title.length
        const isTextInside = doesTextFitSector(value, total, textLength)
        const textPosition = isTextInside
          ? this.getTextPosition({
            isLarge: Boolean(largeArc),
            text: title,
            vector,
            startAngle,
            endAngle,
            center,
          })
          : null

        const isTooltipShown = isHighlighted && !(!isTextInside && isSelected && hasHoveredTooltip)
        const shouldBeShaded = isShaded || isSelected && hasHoveredSector
        const isSaturated = hasHighlightedSector && !shouldBeShaded

        const tooltip = !isTextInside
          ? this.getTooltip({
            positionParams: {
              x1,
              y1,
              x2,
              y2,
              vector,
              textLength,
              startAngle,
              endAngle,
              center,
            },
            isTooltipShown,
            tooltipFontColor,
            title,
            subtitle,
          })
          : null

        return (
          <g key={ `sector${ id }` }>
            <g
              className={ cn(styles.sector, { [styles.shaded]: shouldBeShaded }) }
              style={{ transform: `translate(${ translateX }px, ${ translateY }px)` }}
              onMouseEnter={ handleMouseEnter }
              onClick={ handleClick }
            >
              <path
                d={ dPath }
                fill={ isSaturated ? mainColor : color }
                stroke='#fff'
                strokeWidth={ SECTOR_STROKE_WIDTH }
              />
              { textPosition && (
                <g style={{ transform: `translate(${ textPosition.translationX }, ${ textPosition.translationY })` }}>
                  <text
                    className={ styles.text }
                    fill={ isSaturated ? mainFontColor : fontColor }
                    x={ textPosition.x }
                    y={ textPosition.y }
                  >
                    <tspan>{ textPosition.lines[0] }</tspan>
                    { textPosition.lines.length > 1 && (
                      <tspan x={ textPosition.x } dy='11px'>
                        { textPosition.lines[1] }
                      </tspan>
                    )}
                    <tspan x={ textPosition.x } dy='11px' data-qa-marker='pie-chart-value' data-qa-type='inside'>
                      { subtitle }
                    </tspan>
                  </text>
                </g>
              )}
            </g>
            { tooltip }
          </g>
        )
      })
    )
  }

  private handleClick = (id: Id, isSelected: boolean, isHighlighted: boolean) => {
    const {
      onSectorClick,
      onSectorHover,
    } = this.props

    return onSectorClick
      ? onSectorClick(isSelected ? null : id)
      : IS_MOBILE
        ? onSectorHover(isHighlighted ? null : id)
        : undefined
  }
}

export default PieChart
